import React, { useState } from 'react';
import { createAssignment } from '../../services/auth';

interface CreateAssignmentModalProps {
  studentId: string;
  childName: string;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateAssignmentModal: React.FC<CreateAssignmentModalProps> = ({
  studentId,
  childName,
  onClose,
  onCreated,
}) => {
  const [title, setTitle] = useState('');
  const [targetUnit, setTargetUnit] = useState('1');
  const [targetLesson, setTargetLesson] = useState('');
  const [numQuestions, setNumQuestions] = useState('10');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await createAssignment(studentId, {
        title: title.trim(),
        target_unit: parseInt(targetUnit),
        target_lesson: targetLesson ? parseInt(targetLesson) : null,
        num_questions: parseInt(numQuestions) || 10,
        due_date: dueDate || null,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Assignment for {childName}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Practice Linear Functions"
                maxLength={200}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Unit *</label>
                <select value={targetUnit} onChange={(e) => setTargetUnit(e.target.value)}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((u) => (
                    <option key={u} value={u}>Unit {u}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Lesson (optional)</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={targetLesson}
                  onChange={(e) => setTargetLesson(e.target.value)}
                  placeholder="All lessons"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Number of Questions</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Due Date (optional)</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            {error && <p className="error-message">{error}</p>}
          </div>

          <div className="modal-footer">
            <button type="button" className="secondary-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-btn" disabled={saving}>
              {saving ? 'Creating...' : 'Create Assignment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
